/**
 * The report engine's, the year-close's and the event log's reads
 * (plans 11, 12 — the cockpit's last three tabs).
 *
 * Same rule as every other query module: URLs are declared here where
 * `api-requests.test.ts` can execute them against the Worker catalog, never
 * inside a `useQuery` closure.
 *
 * EXPORTS ARE A NAVIGATION, NOT A FETCH, and `reportExportHref` is a string
 * builder rather than a query for that reason. The response is a file with a
 * `Content-Disposition`; pulling it through `apiGet` would parse it as JSON and
 * throw, and a blob fetched in the browser would then need a synthetic anchor
 * to save anyway. A plain `<a>` on this same origin carries the operator's
 * HttpOnly session cookie, which is the credential these routes want. The one
 * case it does not serve is a `dev:ui` session, where the credential is a
 * bearer set by hand in sessionStorage and a navigation cannot attach it —
 * exports are a `bun --filter @nt/admin dev` or deployed-console affordance.
 *
 * Freshness: every report is an AGGREGATE — a recursive CTE over every journal
 * line in the book — and must not refire because a tab regained focus. The
 * event feed is a LIVE_LIST: it is the log an operator opens to find out what
 * just happened.
 *
 * FIVE OF THESE READS ANSWER UNDER AN INNER KEY, AND THE QUERY IS WHERE IT IS
 * OPENED. `apiGet` unwraps the envelope's `data` and stops — it has no way to
 * know which routes nest a second time — and the report engine, the two tax
 * reads and readiness all answer `data: { report }` / `data: { readiness }`
 * while the close list and the lock dates answer their payload flat. Reading
 * the nested ones off the wrapper is a SILENT failure: nothing throws,
 * `columns` is simply undefined, and a book full of journal lines renders "No
 * rows in this range". `unwrap` is here rather than in each screen because a
 * screen reading a payload it was handed cannot tell the two shapes apart.
 */
import { infiniteQueryOptions } from "@tanstack/react-query";

import { apiGet } from "@/lib/api";
import { keepWhileScope } from "@/lib/list-placeholder";
import { AGGREGATE, LIVE_LIST } from "@/lib/query-freshness";
import { REPORT_PATH_SEGMENTS } from "@/worker/catalog";
import type {
  CloseReadinessResponse,
  Form5472SupportResponse,
  LockDatesResponse,
  PeriodicBasis,
  PeriodicGrain,
  PeriodicReportKey,
  PeriodicReportResponse,
  ReportKey,
  ReportResultPayload,
  VendorFormsResponse,
  YearClosesResponse,
} from "@/types-accounting-reports";

/** The reports that answer AS AT a date rather than FOR a range (spec §8.1). */
const AS_AT_REPORTS = new Set<ReportKey>(["balance_sheet", "trial_balance"]);

/** Every report the engine serves, as the UI's own list. One source, shared. */
export const REPORT_KEYS: readonly ReportKey[] =
  REPORT_PATH_SEGMENTS as readonly ReportKey[];

export interface ReportFilters {
  preset: string;
  /** Only read when `preset` is "custom". */
  from: string;
  to: string;
  compare: string;
  basis: string;
  includeZero: boolean;
  /** Set by a drill-down from a report row. */
  accountId?: string;
  cursor?: string;
}

function bookPath(bookId: string, report: ReportKey, suffix = ""): string {
  return `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/${report}${suffix}`;
}

/**
 * The payload out of the one key its route nests it under.
 *
 * An absent key answers `{}` rather than undefined, for the same reason every
 * field in these payload types is optional: a renamed key should blank the
 * screen it feeds, not throw inside a render.
 */
function unwrap<T extends object, K extends string>(
  key: K,
): (body: Partial<Record<K, T>>) => T {
  return (body) => body?.[key] ?? ({} as T);
}

/**
 * Readiness answers the old `readiness` object AND the checklist fields at the
 * top level (round 3, R7). Read both: the top level wins, the old object fills
 * what an older API leaves out.
 */
export function readinessOf(
  body: CloseReadinessResponse & { readiness?: CloseReadinessResponse },
): CloseReadinessResponse {
  const { readiness, ...top } = body ?? {};
  return { ...(readiness ?? {}), ...top };
}

/**
 * The query string a report request carries.
 *
 * A preset travels BY NAME: spec §8.1 resolves it server-side against the
 * book's fiscal year, and sending the dates the browser resolved would make the
 * console a second authority on when last quarter started. `"none"` and `false`
 * are absences rather than values — `apiGet` drops an empty string and an
 * undefined, which is what keeps an unset filter out of the URL and out of the
 * query key.
 */
export function reportParams(
  report: ReportKey,
  filters: ReportFilters,
): Record<string, string | number | undefined> {
  const custom = filters.preset === "custom";
  const range = AS_AT_REPORTS.has(report)
    ? {
        asAt: custom ? filters.to : undefined,
        preset: custom ? undefined : filters.preset,
      }
    : custom
      ? { from: filters.from, to: filters.to }
      : { preset: filters.preset };

  return {
    ...range,
    compare: filters.compare === "none" ? undefined : filters.compare,
    basis: filters.basis,
    includeZero: filters.includeZero ? "1" : undefined,
    accountId: filters.accountId,
    cursor: filters.cursor,
  };
}

export function reportQuery(
  bookId: string,
  report: ReportKey,
  filters: ReportFilters,
) {
  const params = reportParams(report, filters);
  return {
    ...AGGREGATE,
    // The params ARE the key: a filter change that did not change the key
    // would show the previous report's numbers under the new heading.
    queryKey: ["accounting-report", bookId, report, params] as const,
    // Same report, new filters: the old figures stay (dimmed) until the new
    // ones land. Another report's figures never stand in for this one.
    placeholderData: keepWhileScope(
      (key) => key[1] === bookId && key[2] === report,
    ),
    queryFn: () =>
      apiGet<{ report?: ReportResultPayload }>(
        bookPath(bookId, report),
        params,
      ).then(unwrap<ReportResultPayload, "report">("report")),
  };
}

/** The href an export anchor navigates to. Same filters, plus the format. */
export function reportExportHref(
  bookId: string,
  report: ReportKey,
  format: "csv" | "pdf",
  filters: ReportFilters,
): string {
  const search = new URLSearchParams({ format });
  for (const [key, value] of Object.entries(reportParams(report, filters))) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  return `${bookPath(bookId, report, "/export")}?${search.toString()}`;
}

/**
 * Round 4 (M1): the period report with month/quarter/year columns. Same
 * freshness as every report. The params are the key. `basis` only travels for
 * the profit and loss; the balance sheet and cash flow have none.
 */
export function periodicReportQuery(
  bookId: string,
  report: PeriodicReportKey,
  options: { year: number; grain: PeriodicGrain; basis: PeriodicBasis },
) {
  const params = {
    report,
    year: options.year,
    grain: options.grain,
    basis: report === "profit_and_loss" ? options.basis : undefined,
  };
  return {
    ...AGGREGATE,
    queryKey: ["accounting-periodic-report", bookId, params] as const,
    // A new year, grain or basis keeps the last grid on screen until its own
    // arrives; only the same report in the same book stands in.
    placeholderData: keepWhileScope(
      (key) =>
        key[1] === bookId &&
        (key[2] as { report?: string } | undefined)?.report === report,
    ),
    queryFn: () =>
      apiGet<PeriodicReportResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/periodic`,
        params,
      ),
  };
}

/**
 * Round 4 (M6): the book-wide general ledger for a period. Dates travel with
 * the period name so the server stays the authority on "this month".
 */
export function bookGeneralLedgerQuery(
  bookId: string,
  options: {
    period: string;
    from?: string;
    to?: string;
    accountId?: string;
    cursor?: string;
    limit?: number;
  },
) {
  const custom = options.period === "custom";
  const params = {
    period: options.period,
    from: custom ? options.from : undefined,
    to: custom ? options.to : undefined,
    accountId: options.accountId,
    cursor: options.cursor,
    limit: options.limit,
  };
  return {
    ...AGGREGATE,
    queryKey: ["accounting-gl-period", bookId, params] as const,
    // A new period keeps the last ledger (dimmed) until its own arrives.
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<{ report?: ReportResultPayload }>(
        bookPath(bookId, "general_ledger"),
        params,
      ).then(unwrap<ReportResultPayload, "report">("report")),
  };
}

/** The ledger's page size: the API's ceiling, so a long year needs few round trips. */
export const GENERAL_LEDGER_PAGE = 100;

/**
 * The same read as pages that stack under "Load more". The API pages the
 * book-wide ledger by `cursor` (`nextCursor` on the report); the grid shows
 * the pages loaded so far and the CSV walks the rest (`collectLedgerRows`).
 */
export function bookGeneralLedgerPagesQuery(
  bookId: string,
  options: { period: string; from?: string; to?: string; accountId?: string },
) {
  const custom = options.period === "custom";
  const params = {
    period: options.period,
    from: custom ? options.from : undefined,
    to: custom ? options.to : undefined,
    accountId: options.accountId,
  };
  return infiniteQueryOptions({
    ...AGGREGATE,
    queryKey: ["accounting-gl-period", bookId, "pages", params] as const,
    initialPageParam: "",
    // A new period keeps the last ledger (dimmed) until its own arrives.
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: ({ pageParam }) =>
      apiGet<{ report?: ReportResultPayload }>(
        bookPath(bookId, "general_ledger"),
        {
          ...params,
          cursor: pageParam || undefined,
          limit: GENERAL_LEDGER_PAGE,
        },
      ).then(unwrap<ReportResultPayload, "report">("report")),
    getNextPageParam: (last) => last.nextCursor || undefined,
  });
}

export function form5472SupportQuery(bookId: string, fiscalYear: number) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-5472", bookId, fiscalYear] as const,
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<{ report?: Form5472SupportResponse }>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/form-5472-support`,
        { fiscalYear },
      ).then(unwrap<Form5472SupportResponse, "report">("report")),
  };
}

export function vendorFormsQuery(bookId: string, fiscalYear: number) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-vendor-forms", bookId, fiscalYear] as const,
    placeholderData: keepWhileScope((key) => key[1] === bookId),
    queryFn: () =>
      apiGet<{ report?: VendorFormsResponse }>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/reports/vendor-forms`,
        { fiscalYear },
      ).then(unwrap<VendorFormsResponse, "report">("report")),
  };
}

/**
 * The book's two lock dates.
 *
 * Its own read, because `GET …/close` answers `{ closes }` and carries neither
 * of them. LIVE_LIST for the same reason the close list is: a close moves both
 * dates, so the form must not keep showing the pair from before it.
 */
export function lockDatesQuery(bookId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-locks", bookId] as const,
    queryFn: () =>
      apiGet<LockDatesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/locks`,
      ),
  };
}

/**
 * The book's closes.
 *
 * LIVE_LIST, not AGGREGATE: closing a year changes this list while the operator
 * is looking at it.
 */
export function yearClosesQuery(bookId: string) {
  return {
    ...LIVE_LIST,
    queryKey: ["accounting-closes", bookId] as const,
    queryFn: () =>
      apiGet<YearClosesResponse>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/close`,
      ),
  };
}

export function closeReadinessQuery(bookId: string, fiscalYear: number) {
  return {
    ...AGGREGATE,
    queryKey: ["accounting-close-readiness", bookId, fiscalYear] as const,
    queryFn: () =>
      apiGet<CloseReadinessResponse & { readiness?: CloseReadinessResponse }>(
        `/api/admin/bookkeeping/books/${encodeURIComponent(bookId)}/close/${fiscalYear}/readiness`,
      ).then(readinessOf),
  };
}

/*
 * `bookEventsQuery` is NOT defined here. Plan 14 declared it beside the
 * cockpit's other reads as
 * `bookEventsQuery(bookId, { limit?, kinds?, actor?, cursor? })`, and task 12
 * widens its options with `from`, `to`, `subjectType` and `subjectId` rather
 * than standing up a second query against the same path — two query builders
 * for one route is two cache keys for the same rows.
 */
